import React, { useState } from 'react';
import { FormGroup } from 'reactstrap';
import {
  TextField,
  InputAdornment,
  IconButton,
  styled,
  Box,
} from '@mui/material';
import { Mic, AutoAwesome, Pause } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { AIButton } from 'components/AI/AIButton/AIButton';
import ConfirmationModal from 'common/ConfirmationModal';
import { GradientTypography } from 'components/AI/AIBanner/AIBannerBase/AIBannerBase';
import { useVoiceInput } from './hooks/useVoiceInput';

import { AILoadingState } from './AISharedComponents';

// TypeScript interfaces
interface InlineAiComposeProps {
  generateAiContent?: (
    prompt: string,
  ) => Promise<{ content: string; subject: string; draftUuid: string | null }>;
  isAiEnabled: boolean;
  onContentGenerated: (
    content: string,
    subject?: string,
    draftUuid?: string | null,
  ) => void;
  currentContent?: string;
  disabled?: boolean;
  placeholder?: string;
}

interface FormData {
  prompt: string;
}

// Form validation schema
const composeSchema = yup.object().shape({
  prompt: yup
    .string()
    .trim()
    .required('Please enter a prompt to generate content'),
});

// Styled components for the AI compose section
const AIContainerBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: theme.shape.borderRadius / 2,
  padding: theme.spacing(3),
  marginTop: theme.spacing(5),
}));

const AIHeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const AIContentContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

const InputContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'end',
  marginBottom: theme.spacing(2),
  gap: theme.spacing(1.5),
}));

const InputWrapper = styled('div')({
  flexGrow: 1,
});

const ButtonContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignSelf: 'flex-start',
  gap: theme.spacing(1),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
}));

const AIIcon = styled(AutoAwesome)(({ theme }) => ({
  fontSize: '20px',
  color: theme.palette.primary.main,
  marginRight: theme.spacing(1),
}));

const VoiceIconButton = styled(IconButton, {
  shouldForwardProp: (prop) =>
    prop !== 'isListening' && prop !== 'isProcessingVoice',
})<{ isListening?: boolean; isProcessingVoice?: boolean }>(
  ({ theme, isListening, isProcessingVoice }) => ({
    color:
      isListening || isProcessingVoice
        ? theme.palette.primary.main
        : theme.palette.grey[500],
    backgroundColor: 'transparent',
    borderRadius: '50%',
    transition: 'all 0.3s ease',
    transform: isListening || isProcessingVoice ? 'scale(1.1)' : 'scale(1)',
    opacity: isProcessingVoice ? 0.7 : 1,
  }),
);

const StyledAIButton = styled(AIButton)(({ theme }) => ({
  minHeight: theme.spacing(5),
}));

// Styled components for animations
const PulsingMic = styled(Mic)`
  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.2);
    }
    100% {
      transform: scale(1);
    }
  }

  &.listening {
    animation: pulse 0.8s infinite;
  }
`;

const AnimatedPause = styled(Pause)`
  @keyframes recording {
    0% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.6;
      transform: scale(1.1);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }

  animation: recording 1.5s ease-in-out infinite;
`;

const AnimatedIcon = styled(AutoAwesome)(({ theme }) => ({
  fontSize: theme.spacing(2.25),
  animation: 'pulse 0.8s infinite',
}));

const VoiceIconWrapper = styled('div')(({ theme }) => ({
  fontSize: theme.spacing(2.25),
}));

const InlineAiCompose: React.FC<InlineAiComposeProps> = ({
  generateAiContent,
  isAiEnabled,
  onContentGenerated,
  currentContent = '',
  disabled = false,
  placeholder = 'What would you like to write about? (Be specific for better results)',
}) => {
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [generatedContent, setGeneratedContent] = useState<string>('');
  const [generatedSubject, setGeneratedSubject] = useState<string>('');
  const [generatedDraftUuid, setGeneratedDraftUuid] = useState<string | null>(
    null,
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
    setValue,
    watch,
  } = useForm<FormData>({
    resolver: yupResolver(composeSchema),
    defaultValues: { prompt: '' },
    mode: 'onChange',
  });

  const currentPrompt: string = watch('prompt');

  // Voice input hook
  const {
    isListening,
    isProcessingVoice,
    handleVoiceInput,
    stopRecordingAndCommit,
  } = useVoiceInput({
    currentValue: currentPrompt,
    setValue,
    setError,
    fieldName: 'prompt',
  });

  const onSubmit = async (data: FormData): Promise<void> => {
    if (!generateAiContent) {
      setError('prompt', {
        type: 'manual',
        message: 'AI content generation is not available',
      });
      return;
    }

    setIsGenerating(true);
    clearErrors();

    try {
      const {
        content: aiGeneratedContent,
        subject: aiGeneratedSubject,
        draftUuid,
      } = await generateAiContent(data.prompt);

      setGeneratedContent(aiGeneratedContent);
      setGeneratedSubject(aiGeneratedSubject);
      setGeneratedDraftUuid(draftUuid ?? null);

      // Check if there's existing content
      if (currentContent && currentContent.trim()) {
        setShowConfirmation(true);
      } else {
        // No existing content, use AI content directly
        onContentGenerated(
          aiGeneratedContent,
          aiGeneratedSubject,
          draftUuid ?? null,
        );
        // Keep the prompt for potential regeneration
      }
    } catch (err) {
      // Extract the actual error message from the backend
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to generate content. Please try again.';

      setError('prompt', {
        type: 'manual',
        message: errorMessage,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleConfirmReplace = (): void => {
    onContentGenerated(
      generatedContent,
      generatedSubject,
      generatedDraftUuid ?? null,
    );
    setShowConfirmation(false);
    setGeneratedContent('');
    setGeneratedSubject('');
    setGeneratedDraftUuid(null);
    // Keep the prompt for potential regeneration
  };

  const handleCancelReplace = (): void => {
    setShowConfirmation(false);
    setGeneratedContent('');
    setGeneratedSubject('');
    setGeneratedDraftUuid(null);
  };

  // Handle generate button click - stop recording if in progress, then submit
  const handleGenerateClick = (): void => {
    // Stop recording (if active) and commit transcript, then submit
    stopRecordingAndCommit(() => {
      handleSubmit(onSubmit)();
    });
  };

  // Don't render anything if AI is not enabled
  if (!isAiEnabled) {
    return null;
  }

  return (
    <>
      <FormGroup className="ai-inline-compose">
        <AIContainerBox>
          <AIHeaderContainer>
            <AIIcon />
            <GradientTypography sx={{ fontWeight: '600' }}>
              AI Message Assistant
            </GradientTypography>
          </AIHeaderContainer>
          <AIContentContainer>
            <InputContainer>
              <InputWrapper>
                <StyledTextField
                  id="ai-prompt"
                  size="small"
                  fullWidth
                  variant="outlined"
                  {...register('prompt')}
                  placeholder={placeholder}
                  disabled={isGenerating || disabled}
                  error={Boolean(errors.prompt)}
                  helperText={errors.prompt?.message}
                  multiline
                  minRows={1}
                  maxRows={3}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <VoiceIconButton
                          edge="end"
                          size="medium"
                          disabled={
                            isGenerating || disabled || isProcessingVoice
                          }
                          onClick={handleVoiceInput}
                          title={(() => {
                            if (isProcessingVoice)
                              return 'Processing voice input...';
                            if (isListening) return 'Click to stop recording';
                            return 'Start voice recording';
                          })()}
                          isListening={isListening}
                          isProcessingVoice={isProcessingVoice}
                        >
                          {isProcessingVoice && (
                            <VoiceIconWrapper>
                              <AnimatedIcon />
                            </VoiceIconWrapper>
                          )}
                          {!isProcessingVoice && isListening && (
                            <VoiceIconWrapper>
                              <AnimatedPause />
                            </VoiceIconWrapper>
                          )}
                          {!isProcessingVoice && !isListening && (
                            <VoiceIconWrapper>
                              <PulsingMic />
                            </VoiceIconWrapper>
                          )}
                        </VoiceIconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </InputWrapper>
              <ButtonContainer>
                <StyledAIButton
                  text={isGenerating ? 'Generating...' : 'Generate'}
                  variant="outlined"
                  onClick={handleGenerateClick}
                  disabled={isGenerating || disabled}
                  size="small"
                />
              </ButtonContainer>
            </InputContainer>

            {/* Loading State */}
            {isGenerating && (
              <div className="ai-loading-inline">
                <AILoadingState message="Generating your message..." />
              </div>
            )}
          </AIContentContainer>
        </AIContainerBox>
      </FormGroup>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isHtml
        isOpen={showConfirmation}
        className="customModal customModal--w500 customModal--nopadding"
        messageText={`
          You already have content in your message. Would you like to
          replace it with the AI-generated content?
          <div class="border rounded p-3 bg-light mt-3" style="max-height: 200px; overflow-y: auto;">
            <strong>AI Generated Content:</strong>
            <div style="white-space: pre-wrap; margin-top: 10px;">
              ${generatedContent}
            </div>
          </div>
        `}
        noBtnClick={handleCancelReplace}
        yesBtnClick={handleConfirmReplace}
        yesBtnText="Replace"
        noBtnText="Cancel"
      />
    </>
  );
};

export default InlineAiCompose;
